// @flow
import React, { useEffect, useState } from 'react';
import { get, isEmpty } from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { RouteNames } from 'router';
import { NavigationUtil } from 'utils/NavigationUtil';
import { HomeActions } from 'modules/home/actions';
import { HomeSelector } from 'modules/home/selector';
import { PageLoader } from 'components/molecules/pageLoader';
import RentalDetailBanner from 'components/organisms/rentalDetailBanner';
import { ContactUsSection } from 'components/templates/contactUsSection';
import FooterSection from 'components/templates/footer';
import PageTemplate from 'components/templates/pageTemplate';
import { useViewport } from 'components/templates/viewportProvider';
import type { AppState } from 'modules/rootReducer';
import type { Contact, SiteSettings, Vertical } from 'types/common';
import { FooterActions } from 'modules/footerLink/actions';
import { FooterSelector } from 'modules/footerLink/selector';
import { ProjectActions } from 'modules/project/actions';
import { ProjectBrowserActions } from 'modules/projectBrowser/actions';
import { ProjectSelector } from 'modules/project/selector';
import { ProjectBrowserSelector } from 'modules/projectBrowser/selector';
import { GetInTouchModal } from 'components/templates/getInTouchModal';
import { ShareModal } from 'components/templates/shareModal';
import RenderSEO from 'components/atoms/renderSEO';
import { HeaderButtons } from 'components/atoms/headerButtons';
import RentTerms from 'components/organisms/rentTerms';
import NeighborhoodGuide from 'components/templates/neighborhoodGuide';
import ImageGallerySection from 'components/templates/imageGallerySection';
import ProjectAmenitiesViewControl from 'components/organisms/projectAmenitiesViewControl';
import {
  PROJECT_FILE_DOWNLOAD_URL,
  RENTAL_FILE_DOWNLOAD_URL,
} from 'config/env';
import { CmsPagesSelector } from 'modules/cmsPages/selector';
import { CmsPagesActions } from 'modules/cmsPages/actions';
import OwnHome from 'components/templates/ownHome';
import { RentalActions } from 'modules/rentals/actions';
import { RentalSelector } from 'modules/rentals/selector';
import { ProductActions } from 'modules/product/actions';
import { ProductSelector } from 'modules/product/selector';
import { ProductVariationsSection } from 'components/templates/productVariationsSection';
import { PropertyFeature } from 'components/templates/propertyFeature';
import RecommendedSection from 'components/templates/recommendedSection';

type Props = {
  isLoading: boolean,
  verticals: Array<Vertical>,
  verticalId: number | null,
  getVerticals: () => void,
  siteSettings: SiteSettings,
  getSiteSettings: () => void,
  contact: Contact,
  getContactDetails: () => void,
};

const CompletedProjectRentals = (props: Props) => {
  const {
    isLoading,
    getVerticals,
    verticals,
    verticalId,
    getSiteSettings,
    siteSettings,
    contact,
    getContactDetails,
    getBannerData,
    footerLink,
    getFooterLinkData,
    getPageData,
    recommendedList,
    getGalleryForRental,
    rentalData,
    gallery,
    getRentalDetails,
    banner,
    features,
    getRentalsFeature,
    getRentals,
  } = props;

  const { isMobile } = useViewport();
  let [theme, setTheme] = useState(isMobile ? 'dark-theme' : 'light-theme');
  const rental_url_slug = props.match.params.rentalSlug;
  const [isGetInTouchModalOpen, setIsGetInTouchModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [mapView, setMapView] = useState(false);

  useEffect(() => {
    getContactDetails();
    getVerticals();
    getSiteSettings();
    getFooterLinkData();
    getPageData();
    getBannerData();
    getGalleryForRental(rental_url_slug);
    getRentalDetails(rental_url_slug);
    getRentalsFeature(rental_url_slug);
    getRentals('all');
  }, []);

  if (isLoading) {
    return <PageLoader isMobile={isMobile} />;
  }

  // Get in touch
  const closeGetInTouchModal = () => {
    setIsGetInTouchModalOpen(false);
  };
  // Share
  const closeShareModal = () => {
    setIsShareModalOpen(false);
  };

  if (
    !contact ||
    !siteSettings ||
    !verticals ||
    !footerLink ||
    !recommendedList ||
    !gallery ||
    !rentalData ||
    !banner ||
    !features
  ) {
    return null;
  }

  const CampaignCode = contact.campaignCode ? contact.campaignCode : '';
  const referenceNumber = get(banner, 'reference_no', '');
  const rentalName = get(banner, 'title', '');
  const metaTagSEO = banner.metaTagSEO;

  const updateMapView = (theme, view) => {
    setTheme(theme);
    setMapView(view);
  };

  let pdf = banner && banner.rental_home_brochure;
  let pdfUrl = pdf && `${RENTAL_FILE_DOWNLOAD_URL}${pdf}`;

  const componentList = [
    ...(!isEmpty(banner)
      ? [
          {
            component: RentalDetailBanner,
            data: banner,
            theme: isMobile ? 'dark-theme' : 'light-theme',
            sectionId: 'banner',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Rental Properties',
                breadcrumbLink: RouteNames.rentals,
              },
              {
                breadcrumbName: rentalName,
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(gallery)
      ? [
          {
            component: ImageGallerySection,
            data: {
              images: gallery,
            },
            theme: 'dark-theme',
            sectionId: 'gallery',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Rental Properties',
                breadcrumbLink: RouteNames.rentals,
              },
              {
                breadcrumbName: rentalName,
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: `Gallery`,
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    // ...(!isEmpty(versions)
    //   ? [
    //       {
    //         component: ProductVariationsSection,
    //         data: {
    //           data: versions,
    //           // productName,
    //         },
    //         theme: 'light-theme',
    //         sectionId: 'variations',
    //         breadcrumbList: [
    //           {
    //             breadcrumbName: 'TotalEnvironment',
    //             breadcrumbLink: RouteNames.landing,
    //           },
    //           {
    //             breadcrumbName: 'Rental Properties',
    //             breadcrumbLink: RouteNames.rentals,
    //           },
    //           {
    //             breadcrumbName: `${rentalName} \u2014 Variations`,
    //             breadcrumbLink: '#',
    //           },
    //         ],
    //       },
    //     ]
    //   : []),
    // ...(!isEmpty(projectDetails.project_amenities)
    //   ? [
    //       {
    //         component: ProjectAmenitiesViewControl,
    //         data: {
    //           data: projectDetails,
    //           // pdfUrl,
    //         },
    //         theme: !isMobile ? 'light-theme' : 'dark-theme',
    //         sectionId: 'amenities',
    //         breadcrumbList: [
    //           {
    //             breadcrumbName: 'TotalEnvironment',
    //             breadcrumbLink: RouteNames.landing,
    //           },
    //           {
    //             breadcrumbName: 'Rental Properties',
    //             breadcrumbLink: RouteNames.rentals,
    //           },
    //           {
    //             breadcrumbName: rentalName,
    //             breadcrumbLink: '#',
    //           },
    //           {
    //             breadcrumbName: `Amenities`,
    //             breadcrumbLink: '#',
    //           },
    //         ],
    //       },
    //     ]
    //   : []),
    // ...(!isEmpty(neighbourhoodDetails.list)
    //   ? [
    //       {
    //         component: NeighborhoodGuide,
    //         data: {
    //           data: neighbourhoodDetails,
    //           updateMapView,
    //           mapView,
    //           breadcrumb: rentalName,
    //         },
    //         sectionId: 'neighbourhood-guide',
    //         breadcrumbList: [
    //           {
    //             breadcrumbName: 'TotalEnvironment',
    //             breadcrumbLink: RouteNames.landing,
    //           },
    //           {
    //             breadcrumbName: 'Rental Properties',
    //             breadcrumbLink: RouteNames.rentals,
    //           },
    //           {
    //             breadcrumbName: rentalName,
    //             breadcrumbLink: '#',
    //           },
    //           {
    //             breadcrumbName: `Neighbourhood Guide`,
    //             breadcrumbLink: '#',
    //           },
    //         ],
    //         theme: isMobile ? 'dark-theme' : 'light-theme',
    //       },
    //     ]
    //   : []),
    ...(!isEmpty(features)
      ? [
          {
            component: PropertyFeature,
            data: { features, banner },
            sectionId: 'property-feature',
            theme: 'light-theme',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Rental Properties',
                breadcrumbLink: RouteNames.rentals,
              },
              {
                breadcrumbName: rentalName,
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: 'Property Feature',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(banner)
      ? [
          {
            component: RentTerms,
            data: { banner, setIsGetInTouchModalOpen, pdfUrl },
            sectionId: 'rent-terms',
            theme: 'white-theme',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Rental Properties',
                breadcrumbLink: RouteNames.rentals,
              },
              {
                breadcrumbName: rentalName,
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: 'Rental Terms',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(rentalData)
      ? [
          {
            component: OwnHome,
            data: { rentalData },
            sectionId: 'own-home',
            theme: 'dark-theme',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Rental Properties',
                breadcrumbLink: RouteNames.rentals,
              },
              {
                breadcrumbName: rentalName,
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: 'Own a Similar Home',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),

    ...(!isEmpty(recommendedList)
      ? [
          {
            component: RecommendedSection,
            sectionId: 'recommended',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Homes',
                breadcrumbLink: RouteNames.homes,
              },
              {
                breadcrumbName: rentalName,
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: 'Recommended',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
            data: {
              data: recommendedList,
              type: 'rentals',
            },
          },
        ]
      : []),
    {
      component: ContactUsSection,
      data: {
        contact,
        verticals,
        verticalId,
        referenceNumber,
        rentalName,
        rental: true,
      },
      location: props,
      sectionId: 'contact',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Rental Properties',
          breadcrumbLink: RouteNames.rentals,
        },
        {
          breadcrumbName: 'Contact Us',
          breadcrumbLink: '#',
        },
      ],
      theme: 'light-theme',
    },
    {
      component: FooterSection,
      theme: 'dark-theme',
      sectionId: 'te-links',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Rental Properties',
          breadcrumbLink: RouteNames.rentals,
        },
        {
          breadcrumbName: 'Links',
          breadcrumbLink: '#',
        },
      ],
      data: { siteSettings, footerLink },
    },
  ];

  return (
    <>
      <RenderSEO metaTags={metaTagSEO} currentRouteName={rental_url_slug} />
      <PageTemplate
        theme={theme}
        componentList={componentList}
        updateTheme={e => setTheme(e)}
        verticals={verticals}
        siteSettings={siteSettings}
        headerButtons={
          <HeaderButtons
            shareTheme={'dark'}
            getInTouchTheme={'light'}
            setIsModalOpen={setIsGetInTouchModalOpen}
            setIsShareModalOpen={setIsShareModalOpen}
          />
        }
      />
      <GetInTouchModal
        isOpen={isGetInTouchModalOpen}
        onClosePress={closeGetInTouchModal}
        campaignCode={CampaignCode}
        projectInterested={rentalName}
        salesEmail={contact.email}
        callPostLeadAPI={true}
        referenceNumber={referenceNumber}
        rentalName={rentalName}
        rental={true}
      />
      <ShareModal
        isOpen={isShareModalOpen}
        onClosePress={closeShareModal}
        callPostLeadAPI={true}
      />
    </>
  );
};

const mapStateToProps = (state: AppState, props: any) => {
  const verticalKey = NavigationUtil.getVerticalKeyForLocation(props.location);
  const rentalHomeId =
    state.rentals.banner &&
    state.rentals.banner.home &&
    state.rentals.banner.home[0] &&
    state.rentals.banner.home &&
    state.rentals.banner.home[0].id;

  return {
    isLoading:
      HomeSelector.getContactsLoading(state) ||
      FooterSelector.isLoading(state) ||
      HomeSelector.getVerticalsLoading(state) ||
      HomeSelector.getSiteSettingsLoading(state) ||
      RentalSelector.getRentalFeatureLoading(state) ||
      CmsPagesSelector.isCmsPageLoading(state) ||
      ProjectSelector.isDetailsPageLoading(state) ||
      RentalSelector.getRentalDetailBannerLoading(state),
    contact: HomeSelector.getContactForVertical(state, verticalKey),
    verticalId: HomeSelector.getVerticalIdForKey(verticalKey),
    verticals: HomeSelector.getVerticals(state),
    footerLink: FooterSelector.getFooterData(state),
    siteSettings: HomeSelector.getSiteSettings(state),
    rentalData: CmsPagesSelector.getRentalHome(
      state,
      'Own a Similar Total Environment Home',
      'rentals',
      'true'
    ),
    recommendedList: RentalSelector.getRecommendedList(state, rentalHomeId),
    gallery: ProjectSelector.getGallery(state),
    banner: RentalSelector.getRentalDetailBanner(state),
    features: RentalSelector.getRentalFeature(state),
  };
};

const mapDispatchToProps = dispatch => {
  const {
    getContactDetails,
    getVerticals,
    getSiteSettings,
    getBannerData,
  } = HomeActions;
  const {
    getProjectDetails,
    getParticularProductDetails,
  } = ProjectBrowserActions;
  const { getPageData } = CmsPagesActions;
  const { getFooterLinkData } = FooterActions;
  const {
    getRentals,
    getRentalDetails,
    getRentalsFeature,
    getGalleryForRental,
  } = RentalActions;
  return bindActionCreators(
    {
      getContactDetails,
      getVerticals,
      getSiteSettings,
      getBannerData,
      getFooterLinkData,
      getProjectDetails,
      getPageData,
      getParticularProductDetails,
      getRentalDetails,
      getRentalsFeature,
      getGalleryForRental,
      getRentals,
    },
    dispatch
  );
};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CompletedProjectRentals);
